import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Allgemein',
      questions: [
        {
          q: 'Wie werden die Ankaufspreise ermittelt?',
          a: 'Unsere Ankaufspreise orientieren sich am aktuellen Tageskurs der jeweiligen Edelmetalle (Gold, Silber, Platin, Palladium). Diese werden täglich an internationalen Börsen gehandelt. Wir bieten faire Preise basierend auf Feingehalt und Gewicht.'
        },
        {
          q: 'Benötige ich einen Termin?',
          a: 'Für einen reibungslosen Ablauf und um Wartezeiten zu vermeiden, empfehlen wir dringend eine Terminvereinbarung. Sie können uns telefonisch unter 0231 39083687 oder über unser Kontaktformular erreichen.'
        },
        {
          q: 'Welche Dokumente muss ich mitbringen?',
          a: 'Gemäß Geldwäschegesetz benötigen wir bei jedem Ankauf einen gültigen Personalausweis oder Reisepass. Dies ist gesetzlich vorgeschrieben und dient Ihrer und unserer Sicherheit.'
        },
        {
          q: 'Wie wird ausgezahlt?',
          a: 'Nach erfolgreicher Prüfung und Ihrer Zustimmung zum Angebot können Sie zwischen Barauszahlung (bei Beträgen bis 10.000€) oder Banküberweisung wählen. Die Auszahlung erfolgt sofort bzw. am nächsten Werktag.'
        }
      ]
    },
    {
      category: 'Goldankauf',
      questions: [
        {
          q: 'Kaufen Sie auch Zahngold an?',
          a: 'Ja, wir kaufen Zahngold, Zahnkronen, Zahnbrücken und jeden goldhaltigen Zahnersatz an. Der Wert richtet sich nach dem Goldgehalt, den wir präzise ermitteln.'
        },
        {
          q: 'Was ist mit Bruchgold oder defektem Goldschmuck?',
          a: 'Auch kaputte, beschädigte oder unvollständige Goldgegenstände kaufen wir gerne an. Der Wert bemisst sich am Edelmetallgehalt, nicht am äußeren Zustand.'
        },
        {
          q: 'Wie wird der Goldgehalt geprüft?',
          a: 'Wir verwenden moderne Röntgenfluoreszenz-Analysegeräte und Säureprüfungen. Die Prüfung erfolgt transparent vor Ihren Augen. So können Sie den exakten Feingehalt nachvollziehen.'
        }
      ]
    },
    {
      category: 'Uhrenankauf',
      questions: [
        {
          q: 'Kaufen Sie Uhren auch ohne Papiere?',
          a: 'Ja, wir kaufen Luxusuhren auch ohne Box und Papiere. Vollständige Sets mit Originalverpackung und Zertifikaten erhöhen natürlich den Wert, sind aber keine Voraussetzung für den Ankauf.'
        },
        {
          q: 'Welche Uhrenmarken kaufen Sie an?',
          a: 'Wir kaufen alle gängigen Luxusmarken: Rolex, Omega, Patek Philippe, Audemars Piguet, Breitling, IWC, Cartier, Tag Heuer, Jaeger-LeCoultre, Panerai und viele weitere Premium-Marken.'
        },
        {
          q: 'Was, wenn meine Uhr reparaturbedürftig ist?',
          a: 'Auch defekte oder reparaturbedürftige Luxusuhren kaufen wir an. Der Ankaufspreis wird entsprechend angepasst und berücksichtigt die notwendigen Reparaturkosten.'
        }
      ]
    },
    {
      category: 'Diamantenankauf',
      questions: [
        {
          q: 'Benötige ich Zertifikate für Diamanten?',
          a: 'Zertifikate von GIA, HRD oder IGI sind sehr hilfreich und erhöhen den Wert, da sie Authentizität und Qualität belegen. Aber auch ohne Zertifikat können unsere Experten Ihre Diamanten professionell bewerten.'
        },
        {
          q: 'Nach welchen Kriterien werden Diamanten bewertet?',
          a: 'Wir bewerten nach den international anerkannten 4C: Carat (Gewicht), Cut (Schliff), Color (Farbe) und Clarity (Reinheit). Diese Faktoren bestimmen den Wert eines Diamanten.'
        },
        {
          q: 'Kaufen Sie auch gefasste Diamanten?',
          a: 'Ja, wir kaufen sowohl lose Diamanten als auch gefassten Diamantschmuck (Ringe, Ketten, Ohrringe). Die Diamanten werden separat bewertet.'
        }
      ]
    },
    {
      category: 'Versandankauf',
      questions: [
        {
          q: 'Ist der Versand versichert?',
          a: 'Ja, wenn Sie unser kostenloses DHL-Versandlabel nutzen, ist Ihre Sendung automatisch bis zu 25.000 Euro vollständig versichert. Sie tragen keinerlei Risiko.'
        },
        {
          q: 'Wie lange dauert die Bewertung beim Versandankauf?',
          a: 'Nach Eingang Ihrer Sendung bewerten wir Ihre Wertgegenstände innerhalb von 24 Stunden (Werktags). Sie erhalten dann umgehend unser Angebot per E-Mail oder Telefon.'
        },
        {
          q: 'Was passiert, wenn ich das Angebot ablehne?',
          a: 'Kein Problem! Wenn Sie unser Angebot nicht annehmen möchten, senden wir Ihre Wertgegenstände kostenlos und versichert zurück. Es entstehen Ihnen keine Kosten.'
        }
      ]
    }
  ];

  const toggleFaq = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <>
      <Helmet>
        <title>FAQ - Häufige Fragen | Echt Gold</title>
        <meta name="description" content="Antworten auf häufige Fragen zum Ankauf von Gold, Silber, Uhren und Schmuck. Preise, Ablauf, Bewertung und mehr." />
        <link rel="canonical" href={`${BASE_URL}/faq`} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.flatMap(cat => 
            cat.questions.map(q => ({
              "@type": "Question",
              "name": q.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": q.a
              }
            }))
          )
        })}
      </script>

      <div className="bg-black/90 py-8 border-b border-gold/20">
        <div className="container mx-auto px-4">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">FAQ</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gold-text">Häufig gestellte Fragen</h1>
        </div>
      </div>

      <section className="section section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <p className="text-gray-300 text-lg mb-12 text-center">
              Hier finden Sie Antworten auf die häufigsten Fragen rund um den Ankauf von Edelmetallen, Uhren und Schmuck.
            </p>

            {faqs.map((category, catIdx) => (
              <div key={catIdx} className="mb-12">
                <h2 className="text-2xl font-bold mb-6 gold-text">{category.category}</h2>
                <div className="space-y-4">
                  {category.questions.map((faq, qIdx) => {
                    const key = `${catIdx}-${qIdx}`;
                    const isOpen = openIndex === key;
                    return (
                      <div key={qIdx} className="card">
                        <button
                          onClick={() => toggleFaq(catIdx, qIdx)}
                          className="w-full text-left flex justify-between items-start gap-4"
                          data-testid={`faq-question-${catIdx}-${qIdx}`}
                        >
                          <h3 className="text-lg font-bold gold-text">{faq.q}</h3>
                          {isOpen ? (
                            <ChevronUp className="text-gold flex-shrink-0" size={24} />
                          ) : (
                            <ChevronDown className="text-gold flex-shrink-0" size={24} />
                          )}
                        </button>
                        {isOpen && (
                          <p className="text-gray-300 mt-4 pt-4 border-t border-gold/20">
                            {faq.a}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="card text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 gold-text">Weitere Fragen?</h2>
            <p className="text-gray-300 mb-8">
              Wenn Sie weitere Fragen haben, zögern Sie nicht, uns zu kontaktieren. Wir helfen Ihnen gerne weiter!
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary">Kontakt aufnehmen</Link>
              <a href="tel:023139085116" className="btn-secondary">0231 39085116</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;